import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { PostgrestSingleResponse } from "@supabase/supabase-js";

import { supabase } from "@/integrations/supabase/client";
import { usePipeline } from "@/hooks/use-pipeline";
import { useRehab } from "@/hooks/use-rehab";
import { useRentals, type RentalListing } from "@/hooks/use-rentals";
import { useWorkspace } from "@/hooks/use-workspace";
import { findClosedWonPipeline } from "@/lib/pipeline-display";
import type { MockProperty } from "@/data/mockProperties";

type DbResp<T> = Promise<PostgrestSingleResponse<T>>;

const SETTINGS_TABLE = "property_setting";

/** A closed-won property surfaced in the "List a property for rent" picker. */
export interface ListingCandidate {
  id: string;
  property: MockProperty;
  /**
   * True when a live (non-archived) listing already exists for this property.
   * Such rows are still shown in the picker but rendered disabled so the user
   * can't create a duplicate listing.
   */
  alreadyListed: boolean;
  /**
   * Underwritten rent from `property_setting.monthly_rent` — the value the
   * wizard pre-fills into step 2. Null when the deal has no property_setting
   * row, or the column was never filled in; the field then starts empty rather
   * than showing a stale estimate off `property_data`.
   */
  monthlyRent: number | null;
}

/** The `property_setting` columns the listing picker reads. */
export interface PropertySettingRentRow {
  pipeline_property_id: string;
  monthly_rent: number | null;
}

/**
 * Index `property_setting.monthly_rent` by pipeline_property_id. Numerics come
 * back from Postgres as strings, so coerce and drop anything non-finite (or
 * <= 0) to null so callers get a clean "no rent underwritten" signal.
 * Pure for testing.
 */
export function buildMonthlyRentMap(
  rows: PropertySettingRentRow[],
): Map<string, number> {
  const map = new Map<string, number>();
  for (const row of rows) {
    const rent = Number(row.monthly_rent);
    if (Number.isFinite(rent) && rent > 0) {
      map.set(row.pipeline_property_id, rent);
    }
  }
  return map;
}

/** Minimal shape we need off a `pipeline_properties` (closed-won) row. */
interface ClosedWonRow {
  id: string;
  property_data: unknown;
  /** Explicit ownership flag — only owned properties may be listed. */
  is_owned?: boolean | null;
  /** `pipeline_properties.strategy` (BRRRR / Flip / Rental). */
  strategy?: string | null;
}

/**
 * A flip is meant to be sold, not rented, so it never qualifies for a rental
 * listing. A null/unset strategy is treated as not-a-flip. Pure for testing.
 */
export function isRentableStrategy(strategy: string | null | undefined): boolean {
  return (strategy ?? "").toLowerCase() !== "flip";
}

/**
 * Decide which closed-won properties may receive a rental listing.
 *
 * A property qualifies when it ALL of:
 *   1. is owned (`is_owned === true`),
 *   2. is NOT actively in rehab (no `rehab_projects` row with stage
 *      `in_progress`), and
 *   3. its strategy is not a flip (flips are sold, not rented).
 *
 * Properties with no rehab project, or one that's not-started / completed, are
 * eligible. Properties that already carry a live (non-archived) listing are
 * still returned, flagged `alreadyListed`, so the picker can show them disabled
 * and block duplicate listings.
 *
 * `monthlyRentById` carries `property_setting.monthly_rent` per property (see
 * {@link buildMonthlyRentMap}); a missing entry yields `monthlyRent: null`.
 *
 * Exported as a pure function for unit testing.
 */
export function selectListingCandidates(
  closedWon: ClosedWonRow[],
  inRehabPropertyIds: Set<string>,
  listings: RentalListing[],
  monthlyRentById: Map<string, number> = new Map(),
): ListingCandidate[] {
  return closedWon
    .filter((sp) => sp.is_owned === true)
    .filter((sp) => isRentableStrategy(sp.strategy))
    .filter((sp) => !inRehabPropertyIds.has(sp.id))
    .map((sp) => {
      const forProperty = listings.filter(
        (l) => l.pipeline_property_id === sp.id,
      );
      const alreadyListed =
        forProperty.length > 0 &&
        !forProperty.every((l) => l.status === "archived");
      return {
        id: sp.id,
        property: sp.property_data as unknown as MockProperty,
        alreadyListed,
        monthlyRent: monthlyRentById.get(sp.id) ?? null,
      };
    });
}

/**
 * Owned closed-won properties that may receive a new rental listing — those
 * that are NOT actively in rehab (`rehab_projects.stage = in_progress`) and
 * whose strategy is not a flip (see {@link selectListingCandidates}). Already-
 * listed properties are included with `alreadyListed: true` so the picker can
 * disable them.
 */
export function usePortfolioEligibleForListing() {
  const {
    pipelines,
    getPropertiesByPipeline,
    isLoading: pipelineLoading,
  } = usePipeline();
  const { projects, isLoading: rehabLoading } = useRehab();
  const { listings, isLoading: rentalsLoading } = useRentals();
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.id;

  // Underwritten rent per deal. `property_setting` isn't in the generated
  // Database types yet, so cast at the query boundary (same pattern as
  // use-deal-settings / use-owned-properties).
  const rentQuery = useQuery({
    queryKey: ["property_setting_monthly_rent", workspaceId] as const,
    enabled: Boolean(workspaceId),
    queryFn: async (): Promise<PropertySettingRentRow[]> => {
      if (!workspaceId) return [];
      const { data, error } = (await (
        supabase as unknown as {
          from: (t: string) => {
            select: (s: string) => {
              eq: (c: string, v: string) => DbResp<PropertySettingRentRow[]>;
            };
          };
        }
      )
        .from(SETTINGS_TABLE)
        .select("pipeline_property_id, monthly_rent")
        .eq(
          "workspace_id",
          workspaceId,
        )) as PostgrestSingleResponse<PropertySettingRentRow[]>;
      if (error) throw error;
      return data ?? [];
    },
  });

  const monthlyRentById = useMemo(
    () => buildMonthlyRentMap(rentQuery.data ?? []),
    [rentQuery.data],
  );

  const eligiblePortfolio = useMemo(() => {
    const closedWonPipeline = findClosedWonPipeline(pipelines);
    if (!closedWonPipeline) return [] as ListingCandidate[];
    const closedWon = getPropertiesByPipeline(
      closedWonPipeline,
    ) as unknown as ClosedWonRow[];
    // Properties with an actively-in-progress rehab are blocked from listing.
    const inRehabIds = new Set(
      projects
        .filter((p) => p.stage === "in_progress")
        .map((p) => p.pipeline_property_id),
    );
    return selectListingCandidates(
      closedWon,
      inRehabIds,
      listings,
      monthlyRentById,
    );
  }, [
    pipelines,
    getPropertiesByPipeline,
    projects,
    listings,
    monthlyRentById,
  ]);

  return {
    eligiblePortfolio,
    isLoading:
      pipelineLoading || rehabLoading || rentalsLoading || rentQuery.isLoading,
  };
}
